import React, { useContext, useState } from "react";
import { useTranslation } from "react-i18next";
import { LinkContext } from "../../LinkProvider";

const ACCOUNTS = [
  {
    labelKey: "home.accounts.bronze.label",
    nameKey: "home.accounts.bronze.name",
    minKey: "home.accounts.bronze.min",
    descKey: "home.accounts.bronze.desc",
    btnKey: "btn.select-bronze",
  },
  {
    labelKey: "home.accounts.silver.label",
    nameKey: "home.accounts.silver.name",
    minKey: "home.accounts.silver.min",
    descKey: "home.accounts.silver.desc",
    btnKey: "btn.select-silver",
    dark: true,
    popular: true,
  },
  {
    labelKey: "home.accounts.gold.label",
    nameKey: "home.accounts.gold.name",
    minKey: "home.accounts.gold.min",
    descKey: "home.accounts.gold.desc",
    btnKey: "btn.select-gold",
  },
];

const ChevronLeft = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden>
    <path d="M8.19092 0.494953L0.989907 7.69596L8.19092 14.897M0.989907 7.69596L15.3919 7.69596" stroke="#707070" strokeWidth="1.4" />
  </svg>
);

const ChevronRight = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden>
    <path d="M7.20117 14.8972L14.4023 7.69609L7.20117 0.494995M14.4023 7.69609H8.10744e-05" stroke="#707070" strokeWidth="1.4" />
  </svg>
);

function AccountCard({ labelKey, nameKey, minKey, descKey, btnKey, dark, popular, link, t, mobile = false }) {
  return (
    <div
      className={`relative rounded-[20px] flex flex-col border-b border-[rgba(0,0,0,0.1)] ${
        mobile
          ? "px-[18px] pt-[32px] pb-[22px] min-h-[310px]"
          : `px-[24px] pt-[28px] pb-[28px] ${popular ? "min-h-[380px]" : "min-h-[360px]"}`
      } ${dark ? "bg-[#020202]" : "bg-[#ededed]"} ${popular ? "z-[1]" : ""}`}
    >
      {popular && (
        <div className="absolute -top-[10px] left-1/2 -translate-x-1/2 bg-[#ee5414] border-[2px] border-white rounded-[9999px] px-[16px] py-[4px] whitespace-nowrap">
          <span className="font-['Inter'] font-semibold text-[12px] text-white uppercase tracking-[0.6px] leading-[16px]">
            {t("home.accounts.popular")}
          </span>
        </div>
      )}

      <span className="font-['Inter'] font-normal text-[14px] text-[#717171] tracking-[0.28px] leading-[20px] mb-[4px]">
        {t(labelKey)}
      </span>

      <h3 className={`font-['Montserrat'] font-black ${mobile ? "text-[36px]" : "text-[42px] xl:text-[48px]"} leading-[1.05] mb-[16px] ${dark ? "text-white" : "text-[#020202]"}`}>
        {t(nameKey)}
      </h3>

      <div className={`flex items-baseline gap-[8px] ${mobile ? "mb-[20px]" : "mb-[22px]"} h-[32px]`}>
        <span className="font-['Montserrat'] font-semibold text-[24px] text-[#ee5414] leading-[32px]">
          {t(minKey)}
        </span>
        <span className="font-['Inter'] font-semibold text-[12px] text-[#717171] leading-[16px]">
          {t("home.accounts.min-deposit")}
        </span>
      </div>

      <p className={`font-['Inter'] font-normal ${mobile ? "text-[14px]" : "text-[15px] xl:text-[16px]"} leading-[1.45] text-[#717171] mb-[28px] flex-1`}>
        {t(descKey)}
      </p>

      <a
        href={link}
        className={`w-full flex items-center justify-center rounded-[60px] font-['Inter'] font-medium text-[14px] tracking-[0.28px] leading-[20px] transition-opacity hover:opacity-80 cursor-my ${
          popular
            ? "bg-[#ee5414] text-white py-[12px]"
            : "border border-[#bdbdbd] text-[#bdbdbd] py-[13px]"
        }`}
      >
        {t(btnKey)}
      </a>
    </div>
  );
}

function Accounts() {
  const { t } = useTranslation();
  const { link } = useContext(LinkContext);
  const [activeIndex, setActiveIndex] = useState(1);
  const [direction, setDirection] = useState(1);

  const goTo = (index) => {
    setDirection(index > activeIndex ? 1 : -1);
    setActiveIndex(index);
  };

  const prev = () => {
    setDirection(-1);
    setActiveIndex((i) => Math.max(0, i - 1));
  };

  const next = () => {
    setDirection(1);
    setActiveIndex((i) => Math.min(ACCOUNTS.length - 1, i + 1));
  };

  return (
    <section className="mt">
      <div className="glcontainer">
        <div className="flex flex-col xl:flex-row gap-[20px] xl:gap-[119px] xl:items-center mb-[34px] xl:mb-[70px]">
          <h2 className="font-['Montserrat'] font-black text-[28px] xl:text-[48px] leading-[1] tracking-[-0.92px] text-[#020202] xl:w-[529px] shrink-0">
            {t("home.accounts.title")}
          </h2>
          <p className="font-18-14 xl:max-w-[572px]">{t("home.accounts.desc")}</p>
        </div>

        <div className="hidden sm:grid sm:grid-cols-3 gap-[20px] items-start pt-[18px]">
          {ACCOUNTS.map((account) => (
            <AccountCard key={account.nameKey} t={t} link={link} {...account} />
          ))}
        </div>

        <div className="sm:hidden flex flex-col gap-[14px] pt-[16px]">
          <div
            key={activeIndex}
            className={`swiper-card-enter ${direction > 0 ? "swiper-card-next" : "swiper-card-prev"}`}
          >
            <AccountCard t={t} link={link} mobile {...ACCOUNTS[activeIndex]} />
          </div>

          <div className="flex items-center justify-between bg-[#f7f7f7] rounded-[20px] h-[28px] px-[18px] py-[4px]">
            <button
              onClick={prev}
              disabled={activeIndex === 0}
              className="flex items-center justify-center w-[28px] h-[20px] cursor-my disabled:opacity-30"
              aria-label="Previous account"
            >
              <ChevronLeft />
            </button>

            <div className="flex items-center gap-[8px]">
              {ACCOUNTS.map((_, i) => (
                <button
                  key={i}
                  onClick={() => goTo(i)}
                  className={`w-[8px] h-[8px] rounded-full cursor-my transition-colors duration-200 ${
                    i === activeIndex ? "bg-[#ee5414]" : "bg-[#d9d9d9]"
                  }`}
                  aria-label={`Go to account ${i + 1}`}
                />
              ))}
            </div>

            <button
              onClick={next}
              disabled={activeIndex === ACCOUNTS.length - 1}
              className="flex items-center justify-center w-[28px] h-[20px] cursor-my disabled:opacity-30"
              aria-label="Next account"
            >
              <ChevronRight />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}

export default Accounts;
